import { useCallback, useState } from 'react';

/**
 * § 357 — one rule for whether a control holds its own value.
 *
 * A control is UNCONTROLLED only when the caller passes the `default…` prop (`defaultValue`,
 * `defaultChecked`) and not the value itself; it then keeps its own state, seeded from the
 * default. In every other case the caller owns the value, and with no value at all the
 * control shows `fallback` — exactly what the legacy `value = ''` / `checked` defaults did.
 *
 * Why not "uncontrolled whenever `value` is undefined", the usual rule: this app's fields
 * have always defaulted `value = ''`, so a caller passing a possibly-undefined expression
 * (`value={form.name}`) got a stable empty CONTROLLED input. Flipping those to uncontrolled
 * would change what a screen does. Measured 2026-09-29: no call site passes a `default…`
 * prop, and every one passes the controlled prop — so no existing screen changes mode.
 *
 * Returns `[current, set, uncontrolled]`. `set` updates the internal value only while
 * uncontrolled; the caller's `onChange` is still the control's to call.
 */
export function useControllableState<T>(
  value: T | undefined,
  defaultValue: T | undefined,
  fallback: T,
): readonly [T, (next: T) => void, boolean] {
  const uncontrolled = value === undefined && defaultValue !== undefined;
  const [inner, setInner] = useState<T>(defaultValue ?? fallback);
  const set = useCallback((next: T) => {
    if (uncontrolled) setInner(next);
  }, [uncontrolled]);
  return [uncontrolled ? inner : (value ?? fallback), set, uncontrolled] as const;
}
